<template>
  <v-card>

    <v-snackbar
      :timeout="4000"
      :success="errors === []"
      :info="false"
      :warning="false"
      :error="errors !== []"
      :secondary="false"
      :multi-line="true"
      top
      v-model="snackbar"
      color="primary"
    >
      There are one or more errors on this page.
      <v-btn flat class="white black--text" @click.native="snackbar = false">Close</v-btn>
    </v-snackbar>

    <v-card v-if="nonFieldErrors" class="red lighten-4">
      <v-card-title>
        <span class="headline">Errors</span>
      </v-card-title>
      <v-card-text>
        <p v-for="error in nonFieldErrors" v-html="error"></p>
      </v-card-text>
    </v-card>

    <v-card-title :class="title.cssClass">
      <span class="headline">
        <v-icon :class="title.cssClass">{{title.icon}}</v-icon> {{title.title}} Conversion
      </span>
    </v-card-title>
    <v-card-text>
      <v-stepper v-model="step" vertical>

        <!-- STEP 1: Enter and validate url -->
        <v-stepper-step
          step="1"
          v-bind:complete="step > 1"
        >
          Enter {{title.title}}
        </v-stepper-step>
        <v-stepper-content step="1">
          <form ref="converterForm">
            <div>
              <file-input
                label="Select file"
                @files="handleFiles"
                v-model="geoFileName"
                :rules="[notEmpty]"
                :errorMessages="fieldErrors.geo_file"
                required
              ></file-input>
              <span v-if="fieldErrors.geo_file"><p v-for="err in fieldErrors.geo_file" :key="err">{{err}}</p></span>
            </div>

            <v-select
              v-if="exportFormats"
              label="Select export format"
              hint="The format in which you want the input data to be converted to."
              persistent-hint
              :items="exportFormats"
              v-model="output_format"
              item-value="name"
              item-text="description"
              item-key="name"
              autocomplete
              :rules="[selectValue]"
              :errorMessages="fieldErrors.output_format"
              required
            ></v-select>

            <v-btn @click.native="convert" v-if="!uploadInProgress" class="blue--text darken-1">
              Convert
            </v-btn>
            <v-btn disabled v-if="uploadInProgress" class="grey--text darken-1">
              <v-progress-circular v-if="uploadInProgress" indeterminate class="grey--text">Processing...</v-progress-circular>
            </v-btn>

            <v-card class="grey lighten-5 mt-3" tile>

              <v-card-title>
                <h6 class="header">Optional Parameters</h6>
              </v-card-title>

              <v-card class="blue-grey lighten-5 ma-0" tile>
                <v-card-text class="ma-0 pt-0">
                  <h6 class="subheader ma-0 pa-0">Input/Output Parameters</h6>
                  <v-form ref="OptionsForm">
                    <v-text-field
                      label="Source CRS (EPSG code)"
                      hint="Only needed when it can not be determined from input. For example `EPSG:4326` (without quotes)."
                      v-model="input_crs"
                      :errorMessages="fieldErrors.input_crs"
                      persistent-hint
                    ></v-text-field>
                    <v-text-field
                      label="Output CRS (EPSG code)"
                      hint="For example `EPSG:4326` (without quotes)."
                      v-model="output_crs"
                      :errorMessages="fieldErrors.output_crs"
                      persistent-hint
                    ></v-text-field>
                    <v-text-field
                      v-if="conversionType === 'raster'"
                      label="Output Image width"
                      hint="Image width in pixels, height will be selected automatically"
                      v-model="output_image_width"
                      :errorMessages="fieldErrors.output_image_width"
                      persistent-hint
                    ></v-text-field>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-card>
          </form>

        </v-stepper-content>

        <!-- STEP 2: Done -->
        <v-stepper-step
          step="2"
          v-bind:complete="step > 2"
        >
          Submitted.
        </v-stepper-step>
        <v-stepper-content step="2">
          <p>
            Please collect your result below. This might take a while, depending on the velocity of the service
            and the size of the data.
          </p>
          <v-btn color="primary" @click="resetForm();resetErrors();step=1">Start a new conversion.</v-btn>
        </v-stepper-content>
      </v-stepper>
    </v-card-text>
  </v-card>
</template>

<script>
  import _ from 'lodash';
  import FileInput from '@/components/converter/input/FileInput';
  import GeoResults from '@/components/converter/output/ConversionResults';
  import axios from '@/components/helpers/axiosConfig';
  import geoTypes from '@/components/converter/helpers/geoTypes';

  export default {
    name: 'geo-converter',
    props: {
      conversionType: {
        required: true,
        type: String,
      },
    },
    watch: {
      errors: () => { this.snackbar = true; },
    },
    created() {
      axios.get(`/api/formats/${this.conversionType}/`)
        .then((response) => {
          this.exportFormats = response.data.options;
        })
        .catch((e) => {
          const aboutUsPage = this.$router.resolve('/about-us');
          this.errors.push(`A problem with the service occurred. Please try again. If the problem persists
             feel free to <a href="${aboutUsPage.href}">contact us</a>. Complete error was:
             <pre>${e}</pre>`);
          this.snackbar = true;
        });
    },
    components: {
      'file-input': FileInput,
      'conversion-results': GeoResults,
    },
    methods: {
      isFormValid() {
        let valid = false;
        valid = this.geo_file && this.geo_file.name && this.geo_file.name !== '';
        valid = valid && this.output_format && this.output_format !== '';
        return valid;
      },
      handleFiles(files) {
        // Ugly, but this is OK, since we only allow single file selection.
        if (files && files.length > 0) {
          [this.geo_file] = files;
        }
      },
      convert() {
        if (!this.isFormValid()) {
          this.snackbar = true;
          return;
        }
        this.uploadInProgress = true;
        let formData = {
          input_crs: this.input_crs,
          output_format: this.output_format,
          output_crs: this.output_crs,
          output_image_width: this.output_image_width,
        };
        formData = new FormData();
        formData.append('geo_file', this.geo_file);
        formData.append('conversion_type', this.conversionType);
        formData.append('output_format', this.output_format);
        if (this.input_crs) formData.append('input_crs', this.input_crs);
        if (this.output_crs) formData.append('output_crs', this.output_crs);
        if (this.output_image_width) formData.append('output_image_width', this.output_image_width);
        this.resetErrors();
        axios.post('/api/converter/', formData)
          .then((response) => {
            this.step = 2;
            this.uploadInProgress = false;
            this.$store.commit('addConversion', response.data);
            this.resetForm();
          })
          .catch((error) => {
            this.uploadInProgress = false;
            if (error.response) {
              // The request was made and the server responded with a status code
              // that falls out of the range of 2xx
              let errorData = error.response.data;
              if (error.response.status === 413) {
                // (Request Entity Too Large)
                errorData = 'File size limit reached. Please reduce the data size and try it again' +
                  'with a smaller file.';
              }
              this.errors = errorData;
            } else if (error.request) {
              const aboutUsPage = this.$router.resolve('/about-us');
              this.errors = [
                `A problem with the service occurred. Please try again. If the problem persists
                 feel free to <a href="${aboutUsPage.href}">contact us</a>. Complete error was:
                 <pre>${error}</pre>`,
              ];
            } else {
              this.errors = [`Unknown error: ${error}`];
            }
            this.snackbar = true;
          });
      },
      showError(errorList) {
        if (!(errorList instanceof Array)) {
          return `<span class="error">${errorList}</span>`;
        }
        let errorMessages = '';
        errorList.forEach((error) => {
          const formattedError = error.replace('\n', '<br>');
          errorMessages += `<span class="error">${formattedError}</span>`;
        });
        return errorMessages;
      },
      resetErrors() {
        this.errors = null;
      },
      selectValue(val) {
        return (val && val !== '') || 'Please select an option.';
      },
      notEmpty(val) {
        return (val && val !== '') || (val && val.text && val.text.length > 0) || 'Please fill out this field.';
      },
      resetForm() {
        this.geo_file = null;
        this.geoFileName = null;
        this.input_crs = null;
        this.output_format = null;
        this.output_crs = null;
        this.output_image_width = null;
        this.$refs.converterForm.reset();
      },
      extractErrors() {
        let nonFieldErrors = {};
        let fieldErrors = {};
        const fieldNames = [
          'geo_file',
          'input_crs',
          'output_format',
          'output_crs',
          'output_image_width',
        ];
        const errors = Object.assign({}, this.errors);
        if (errors) {
          fieldNames.forEach((fieldName) => {
            const fieldError = errors[fieldName];
            if (fieldError) {
              fieldErrors[fieldName] = fieldError;
              delete errors[fieldName];
            }
          });
          if (!_.isEmpty(errors) && Array.isArray(errors)) {
            nonFieldErrors = [errors];
          } else {
            nonFieldErrors = errors;
          }
        }
        if (_.isEmpty(fieldErrors)) {
          fieldErrors = null;
        }
        if (_.isEmpty(nonFieldErrors)) {
          nonFieldErrors = null;
        }
        return [fieldErrors, nonFieldErrors];
      },
    },
    data() {
      return {
        showAdvancedOptions: false,
        geo_file: null,
        input_crs: null,
        output_format: null,
        output_crs: null,
        output_image_width: null,
        exportFormats: null,
        snackbar: false,
        errors: null,
        geoFileName: null,
        uploadInProgress: false,
        step: 1,
      };
    },
    computed: {
      fieldErrors() {
        const fieldErrors = this.extractErrors()[0];
        if (fieldErrors) {
          return fieldErrors;
        }
        return {};
      },
      nonFieldErrors() {
        return this.extractErrors()[1];
      },
      title() {
        const conversionType = this.conversionType.toLowerCase();
        return geoTypes[conversionType];
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/converter/input/GeoConverterFile.vue